import React from 'react'
import PropTypes from 'prop-types'
import {Box, SkeletonText} from '@chakra-ui/react'
import {renderLayoutsSwitch, renderComponentsSwitch} from '../../../utils/contentRenderer'
import useSizeGuidesLazyContent from './use-size-guides-lazy-content'

/**
 * GDISizeGuidesBlock
 *
 * Renders the content area of a size-guide block. Used both:
 *  1. On the dedicated size-guide CMS page (inside GDITabsLayoutBlock tabs), and
 *  2. On PDP via SizeChartDrawer (matched by CategoryId to product.c_sizeChartWeb).
 *
 * CMS shape:
 *   CategoryId                 — slug matching product.c_sizeChartWeb, or a
 *                                comma-separated list of them (used by PDP only)
 *   GDISizeGuidesBlockContent  — content area items (tables, text blocks, images…)
 *
 * When this block is nested deep inside the page query (e.g. inside GDITabsLayoutBlock
 * tabs on the size-guide page), Optimizely Graph's MaxExpansionDepth cap causes inner
 * content items to arrive with Expanded: {} (no __typename). The useSizeGuidesLazyContent
 * hook detects this and re-fetches the block by its own ContentLink.Id, resetting the
 * expansion-depth counter so all nested content resolves correctly.
 */
const GDISizeGuidesBlock = ({_contentLinkId, GDISizeGuidesBlockContent}) => {
    const {contentArea, isLoading} = useSizeGuidesLazyContent({
        blockId: _contentLinkId,
        contentArea: GDISizeGuidesBlockContent
    })

    // Show skeleton while the lazy fetch is resolving depth-cap content.
    // isLoading is only true when needsLazyLoad was detected (depth-cap entries present).
    if (isLoading) {
        return (
            <Box padding={4}>
                <SkeletonText noOfLines={6} spacing={4} skeletonHeight={3} />
            </Box>
        )
    }

    const items = contentArea ?? []
    if (items.length === 0) return null

    return (
        <>
            {items.map((item, i) => {
                const exp = item?.ContentLink?.Expanded
                // Skip null/undefined AND depth-cap entries (truthy object with no __typename).
                if (!exp || !exp.__typename) return null
                return (
                    <React.Fragment key={i}>
                        {renderLayoutsSwitch(exp) || renderComponentsSwitch(exp)}
                    </React.Fragment>
                )
            })}
        </>
    )
}

GDISizeGuidesBlock.propTypes = {
    _contentLinkId: PropTypes.number,
    CategoryId: PropTypes.string,
    GDISizeGuidesBlockContent: PropTypes.arrayOf(PropTypes.object)
}

export default GDISizeGuidesBlock
